import type { CSSProperties, FC } from 'react';
import Btn from 'src/components-v2/Inputs/Button/Btn';
import Container from 'src/components-v2/Layout/Container/Container';
import Column from 'src/components-v2/Layout/Grid/Column';
import Row from 'src/components-v2/Layout/Grid/Row';
import { useCriticalRenderContext } from 'src/hooks/useCriticalRenderContext';
import Stack from 'threads5/Stack';
import styled from 'threads5/styles/styled';
import ResponsiveImage from '../ResponsiveImage';
import SolutionsBentoCard from './SolutionsBentoCard';

const StyledSection = styled(Stack)(({ theme }) => {
  return {
    marginTop: '85px',
    gap: theme.spacing(8),
    [theme.breakpoints.up('md')]: {
      marginTop: '47px',
    },
    [theme.breakpoints.up('lg')]: {
      marginTop: '130px',
    },
  };
});

const StyledHeader = styled(Stack, {
  shouldForwardProp: (prop) => {
    return prop !== 'isPlaidSansLoaded';
  },
})<{ isPlaidSansLoaded: boolean }>(({ theme, isPlaidSansLoaded }) => {
  return {
    alignItems: 'flex-start',
    gap: theme.spacing(3),
    opacity: isPlaidSansLoaded ? 1 : 0,
    transition: 'opacity .3s ease-in-out',
    [theme.breakpoints.up('lg')]: {
      gap: theme.spacing(4),
    },
  };
});

const StyledHeading = styled('h2')(({ theme }) => {
  return {
    margin: 0,
    fontFamily: theme.typography.plaidSans,
    color: theme.palette['base/sky/1000'],
    fontSize: '3.2rem',
    fontWeight: 500,
    lineHeight: '114%',
    letterSpacing: '-0.2rem',
    textWrap: 'balance',
    br: { display: 'none' },
    [theme.breakpoints.up('md')]: {
      br: { display: 'block' },
    },
    [theme.breakpoints.up('lg')]: {
      fontSize: '5.4rem',
      letterSpacing: '-0.2rem',
    },
    [theme.breakpoints.up('xl')]: {
      fontSize: '5.8rem',
    },
  };
});

type BentoCardImageSources = { xs: string; md: string; lg: string };

const BentoCardForegroundImageStylesHoverUp: CSSProperties = {
  translate: '0 -10px',
};

const BentoCardForegroundImageStylesHoverScale: CSSProperties = {
  scale: '1.05',
};

const SolutionsBentoCards: ReadonlyArray<{
  primaryCopy: string;
  secondaryCopy: string;
  href: string;
  lgHeight: number;
  lgWidth: number;
  backgroundSrcs?: BentoCardImageSources;
  foregroundSrcs: BentoCardImageSources;
  foregroundImageStyles?: CSSProperties;
  foregroundImageHoverStyles?: CSSProperties;
}> = [
  {
    primaryCopy: 'Bank payments',
    secondaryCopy: 'designed for fast connection and high conversion',
    href: '/use-cases/inbound-bank-payments/',
    lgHeight: 584,
    lgWidth: 12,
    backgroundSrcs: {
      xs: 'https://images.ctfassets.net/ss5kfr270og3/6NaWfMcuLsaaxUKC8Lna1d/2d50503595bf5266080b582e6f23f08c/homepage-solutions-pbb-background-mobile.png?fm=webp&q=70',
      md: 'https://images.ctfassets.net/ss5kfr270og3/u2ubv096FBgilg96JNohq/007d2c46240e617458bac68c61b2e995/homepage-solutions-pbb-background-tablet.png?fm=webp&q=70',
      lg: 'https://images.ctfassets.net/ss5kfr270og3/2VBqUYyDYezsT9oSBZbwgp/27e8e214d8c223854968bf291e8e23b8/homepage-solutions-pbb-background.png?fm=webp&q=70',
    },
    foregroundSrcs: {
      xs: 'https://images.ctfassets.net/ss5kfr270og3/3neijpK6poaHTeX1rIUW9Q/f6de13b9559c3255f1def1e409808aaa/homepage-solutions-pbb-foreground-mobile.png?fm=webp&q=70',
      md: 'https://images.ctfassets.net/ss5kfr270og3/6LCH8M0ujMBGX5Csnboxjh/1794310dee78d955c1441ba472a0637a/homepage-solutions-pbb-foreground-tablet.png?fm=webp&q=70',
      lg: 'https://images.ctfassets.net/ss5kfr270og3/5oZSZrxjfKxQTD5HGUFvZa/e312269ddab490592839e9888c78a810/homepage-solutions-pbb-foreground.png?fm=webp&q=70',
    },
    foregroundImageHoverStyles: BentoCardForegroundImageStylesHoverUp,
  },
  {
    primaryCopy: 'Credit scoring & underwriting',
    secondaryCopy: 'that reflects real-time financial behavior',
    href: '/check/underwriting/',
    lgHeight: 584,
    lgWidth: 12,
    foregroundSrcs: {
      xs: 'https://images.ctfassets.net/ss5kfr270og3/1ohoFGwbxGuu9adRUI8UDX/7a90b8ba683f1e2ab3f693193baec2bd/hero-ls-asset.png?fm=webp&q=70&w=660',
      md: 'https://images.ctfassets.net/ss5kfr270og3/1ohoFGwbxGuu9adRUI8UDX/7a90b8ba683f1e2ab3f693193baec2bd/hero-ls-asset.png?fm=webp&q=70&w=740',
      lg: 'https://images.ctfassets.net/ss5kfr270og3/1ohoFGwbxGuu9adRUI8UDX/7a90b8ba683f1e2ab3f693193baec2bd/hero-ls-asset.png?fm=webp&q=70',
    },
    foregroundImageStyles: {
      objectFit: 'contain',
      objectPosition: 'bottom center',
    },
    foregroundImageHoverStyles: BentoCardForegroundImageStylesHoverScale,
  },
  {
    primaryCopy: 'Fraud protection',
    secondaryCopy: 'built on millions of data points',
    href: '/use-cases/fraud-and-risk-prevention/',
    lgHeight: 619,
    lgWidth: 8,
    backgroundSrcs: {
      xs: 'https://images.ctfassets.net/ss5kfr270og3/2LWbhEC3byEPVbNH14LTJ2/2842ad93516dc6c39f445d7a13027bf7/homepage-solutions-fraud-background-mobile.png?fm=webp&q=70',
      md: 'https://images.ctfassets.net/ss5kfr270og3/55Ci2t0tLmQIX8F41jz4ie/5b687ec6fc276702915bd9c016fb1620/homepage-solutions-fraud-background-tablet.png?fm=webp&q=70',
      lg: 'https://images.ctfassets.net/ss5kfr270og3/7924EVvCFUmnRoZ9Vc8icw/a16de77516597adbe3f25f2b5e19a8c6/homepage-solutions-fraud-protection-background-v2.png?fm=webp&q=70',
    },
    foregroundSrcs: {
      xs: 'https://images.ctfassets.net/ss5kfr270og3/3CFOwl9tGo2CVQH9HJyLwE/34755ab77b89eebfa38ee1db94edbf6f/homepage-solutions-fraud-foreground-mobile.png?fm=webp&q=70',
      md: 'https://images.ctfassets.net/ss5kfr270og3/72iYVg3pPIkcpL6FCABzi0/a5f19bf259e7ee868b19d2ac1d50d044/homepage-solutions-fraud-foreground-tablet.png?fm=webp&q=70',
      lg: 'https://images.ctfassets.net/ss5kfr270og3/5rC6bjwGMM7YXFMymdpxrk/79444bdf2c806dfad76ce37d7ed6e357/homepage-solutions-fraud-protection-foreground-v2.png?fm=webp&q=70',
    },
    foregroundImageHoverStyles: BentoCardForegroundImageStylesHoverScale,
  },
  {
    primaryCopy: 'Instant onboarding',
    secondaryCopy: 'with the most trusted financial flow',
    href: '/use-cases/instant-onboarding-identity-verification/',
    lgHeight: 619,
    lgWidth: 8,
    backgroundSrcs: {
      xs: 'https://images.ctfassets.net/ss5kfr270og3/10KjSWjFK2v4Zn4gB0BMVK/615eb13bd693542fb2a0eccc9110d2df/homepage-solutions-onboarding-background-mobile.png?fm=webp&q=70',
      md: 'https://images.ctfassets.net/ss5kfr270og3/l7xNCT5vRAgAFm7ktVDUh/23cce6b2f92169e9706c39e75569558c/homepage-solutions-onboarding-background-tablet.png?fm=webp&q=70',
      lg: 'https://images.ctfassets.net/ss5kfr270og3/4BkJrP7wZI3KjVNI3HfYDj/b86e20af00497c299872b7b2f9a0996f/homepage-solutions-onboarding-background-v2.png?fm=webp&q=70',
    },
    foregroundSrcs: {
      xs: 'https://images.ctfassets.net/ss5kfr270og3/5SXkQTO9mqdNyVU4TaThLF/2dbf256a386b2ee0559bd16aa982f0df/homepage-solutions-onboarding-foreground-mobile.png?fm=webp&q=70',
      md: 'https://images.ctfassets.net/ss5kfr270og3/6hA5kFIFUxOeKuNifyJHSD/32016762fd40483628011a15133077be/homepage-solutions-onboarding-foreground-tablet.png?fm=webp&q=70',
      lg: 'https://images.ctfassets.net/ss5kfr270og3/7z8aVHVRD6meVjw2DPXpu4/942fc7a997c05b04386c843a569482e1/homepage-solutions-onboarding-foreground.png?fm=webp&q=70',
    },
    foregroundImageHoverStyles: BentoCardForegroundImageStylesHoverUp,
  },
  {
    primaryCopy: 'Personal financial management',
    secondaryCopy: 'built on trusted, actionable data',
    href: '/use-cases/personal-financial-insights/',
    lgHeight: 619,
    lgWidth: 8,
    backgroundSrcs: {
      xs: 'https://images.ctfassets.net/ss5kfr270og3/3kiKIJ1GbOW9OOH3LhIvPz/03a06ac738632ea30884f95994bffd2b/homepage-solutions-pfm-background-mobile.png?fm=webp&q=70',
      md: 'https://images.ctfassets.net/ss5kfr270og3/4dgC4ouo5xDL4Kog65OOGg/769c28fc09b092a2f762ab031702c9b5/homepage-solutions-pfm-background-tablet.png?fm=webp&q=70',
      lg: 'https://images.ctfassets.net/ss5kfr270og3/3gt9kL5SspXTJX1eipqoh4/3375f55fbc26ed9ea87f98a1e04623cf/homepage-solutions-pfm-background-v2.png?fm=webp&q=70',
    },
    foregroundSrcs: {
      xs: 'https://images.ctfassets.net/ss5kfr270og3/64BJ11HVtxR9QyHqpolWng/f7a24833d1ad5547668ee06bc60fd3c8/homepage-solutions-pfm-foreground-mobile.png?fm=webp&q=70',
      md: 'https://images.ctfassets.net/ss5kfr270og3/3JsNjcwo7EQOHX6oSx1S60/a0bc8ee54c60a06cb710ca454ac99aee/homepage-solutions-pfm-foreground-tablet.png?fm=webp&q=70',
      lg: 'https://images.ctfassets.net/ss5kfr270og3/3UG6PMmet354V7RfLHIcDv/1bcc57019966868d91bd997b2ce500f7/homepage-solutions-pfm-foreground.png?fm=webp&q=70',
    },
    foregroundImageHoverStyles: BentoCardForegroundImageStylesHoverScale,
  },
];

const SolutionsBento: FC = () => {
  const { isPlaidSansLoaded } = useCriticalRenderContext();

  return (
    <Container>
      <Row>
        <Column xs={22} xsOffset={1}>
          <StyledSection>
            <StyledHeader isPlaidSansLoaded={isPlaidSansLoaded}>
              <StyledHeading>
                Powered by the largest financial network. <br />
                Built for every financial need.
              </StyledHeading>

              <Btn href='/products/' variant='primary'>
                See all products
              </Btn>
            </StyledHeader>

            <Row spacing={{ xs: 3, xxl: 4 }}>
              {SolutionsBentoCards.map(
                ({ lgWidth, backgroundSrcs, foregroundSrcs, ...card }) => {
                  return (
                    <Column key={card.href} xs={24} md={12} lg={lgWidth}>
                      <SolutionsBentoCard
                        {...card}
                        backgroundImage={
                          backgroundSrcs && (
                            <ResponsiveImage
                              srcs={backgroundSrcs}
                              alt=''
                              loading='lazy'
                            />
                          )
                        }
                        foregroundImage={
                          <ResponsiveImage
                            srcs={foregroundSrcs}
                            alt=''
                            loading='lazy'
                          />
                        }
                      />
                    </Column>
                  );
                },
              )}
            </Row>
          </StyledSection>
        </Column>
      </Row>
    </Container>
  );
};

export default SolutionsBento;
